import React, { useState } from 'react';
import { MessageCircle, Calendar, Calculator, Phone, Check, X, Settings, ExternalLink } from 'lucide-react';
import { chatbotMessaging } from '@/services/chatbotMessagingService';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  enabled: boolean;
  configured: boolean;
  configUrl?: string;
}

export const ChatbotIntegrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'calculator',
      name: 'Kalkulator wycen',
      description: 'Automatyczne wyceny produktów w czasie rzeczywistym',
      icon: Calculator,
      enabled: true,
      configured: true
    },
    {
      id: 'calendar',
      name: 'Kalendarz spotkań',
      description: 'Umawianie spotkań bezpośrednio z czatu',
      icon: Calendar,
      enabled: true,
      configured: true
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      description: 'Obsługa klientów przez WhatsApp',
      icon: MessageCircle,
      enabled: false,
      configured: false,
      configUrl: '/admin/integrations/whatsapp'
    },
    {
      id: 'messenger',
      name: 'Facebook Messenger',
      description: 'Czat na stronie Facebook',
      icon: MessageCircle,
      enabled: false,
      configured: false,
      configUrl: '/admin/integrations/messenger'
    },
    {
      id: 'sms',
      name: 'Bramka SMS',
      description: 'Powiadomienia SMS dla klientów',
      icon: Phone,
      enabled: false,
      configured: false,
      configUrl: '/admin/integrations/sms'
    }
  ]);

  const [showInstructions, setShowInstructions] = useState<string | null>(null);

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? { ...integration, enabled: !integration.enabled }
          : integration
      )
    );
  };

  const getChannelType = (id: string) => {
    const channelMap: Record<string, any> = {
      whatsapp: 'whatsapp',
      messenger: 'messenger',
      sms: 'sms'
    };
    return channelMap[id];
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Integracje czatbota</h2>
        <p className="text-gray-400">
          Rozszerz możliwości czatbota o dodatkowe funkcje i kanały komunikacji
        </p>
      </div>

      <div className="grid gap-4">
        {integrations.map(integration => {
          const Icon = integration.icon;
          return (
            <div
              key={integration.id}
              className="bg-gray-700 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${
                  integration.enabled ? 'bg-orange-500/20' : 'bg-gray-600'
                }`}>
                  <Icon className={`w-6 h-6 ${
                    integration.enabled ? 'text-orange-500' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    {integration.name}
                    {integration.configured && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                        Skonfigurowane
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-400">{integration.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!integration.configured && integration.configUrl && (
                  <button
                    onClick={() => setShowInstructions(integration.id)}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => toggleIntegration(integration.id)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    integration.enabled ? 'bg-orange-500' : 'bg-gray-600'
                  } ${!integration.configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!integration.configured && !['calculator', 'calendar'].includes(integration.id)}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      integration.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal z instrukcjami */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                Konfiguracja: {integrations.find(i => i.id === showInstructions)?.name}
              </h3>
              <button
                onClick={() => setShowInstructions(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="prose prose-invert max-w-none">
                <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
                  {chatbotMessaging.getSetupInstructions(getChannelType(showInstructions))}
                </pre>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowInstructions(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statystyki */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-orange-500">2/5</p>
          <p className="text-sm text-gray-400">Aktywne integracje</p>
        </div>
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-500">100%</p>
          <p className="text-sm text-gray-400">Dostępność czatbota</p>
        </div>
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">247</p>
          <p className="text-sm text-gray-400">Rozmów dzisiaj</p>
        </div>
      </div>
    </div>
  );
};